import React, { useEffect, useRef } from "react";
import { RouteComponentProps } from "react-router";
import { Box } from "@material-ui/core";
import { CAP1_HOTEL_CFAR_ADD_ON_CHOICE } from "redmond";
import {
  goToShop,
  goToCheckout,
} from "../../../../shop/utils/queryStringHelpers";
import { MobileAddOnCustomizeConnectorProps } from "./container";
import { AddOnCustomizeStep, AddOnCustomizeStepAlias } from "./types";
import {
  CfarOption,
  StandAloneCfarDetails,
} from "../cfar/StandAloneCfarDetails";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { Cap1HotelCfarHtsMerchandising } from "../cfar/Cap1HotelCfarHtsMerchandising";
import { isHtsCfarOffer } from "../utils";

export interface IMobileAddOnCustomizeProps
  extends MobileAddOnCustomizeConnectorProps,
    RouteComponentProps {
  // note: this callback function is used to overwrite the default (final) on-continue behaviour
  goToNextPage?: () => void;
  goBack?: () => void;
  currentStep: AddOnCustomizeStepAlias;
  setCurrentStep: React.Dispatch<React.SetStateAction<AddOnCustomizeStepAlias>>;
}

const addOnCustomizeSteps = {
  [AddOnCustomizeStepAlias.GoBack]: AddOnCustomizeStep.GoBack,
  [AddOnCustomizeStepAlias.AddOn1]: AddOnCustomizeStep.CFAR,
  [AddOnCustomizeStepAlias.GoToCheckout]: AddOnCustomizeStep.GoToCheckout,
};

export const MobileAddOnCustomize = (props: IMobileAddOnCustomizeProps) => {
  const {
    history,
    cfarQuoteV2,
    cancellationPolicy,
    isCfarAvailable,
    goToNextPage,
    goBack,
    currentStep,
    setCurrentStep,
    refundableRoomProductCorrespondingToChosenRoom,
  } = props;
  const prevStep = useRef<AddOnCustomizeStepAlias>(currentStep);

  const defaultGoToNextPage = () => {
    goToCheckout({
      history,
      shouldAddCfarOfferParam: isHtsCfarOffer(cfarQuoteV2)
    });
  };

  const defaultGoBack = () => {
    goToShop({ history });
  };

  const onContinue = () => {
    setCurrentStep((step) => step + 1);
  };

  useEffect(
    () => () => {
      setCurrentStep(AddOnCustomizeStepAlias.AddOn1);
    },
    []
  );

  useEffect(() => {
    const processStep = (skipCondition: boolean) => {
      /*
        note: when a step isn't enabled (e.g. cfar experiment is turned off or the cfar offer is not available),
        that step needs to be skipped.
      */
      if (skipCondition) {
        const stepAfter = currentStep + 1;
        const stepBefore = currentStep - 1;

        // note: take into account the direction of the navigation, i.e. if from the step after, then skip to before, and vice versa.
        if (prevStep.current === stepAfter) {
          setCurrentStep(stepBefore);
        } else {
          setCurrentStep(stepAfter);
        }
      } else {
        prevStep.current = currentStep;
      }
    };

    switch (addOnCustomizeSteps[currentStep]) {
      case AddOnCustomizeStep.GoBack:
        prevStep.current = currentStep;
        (goBack ?? defaultGoBack)();
        break;
      case AddOnCustomizeStep.GoToCheckout:
        prevStep.current = currentStep;
        (goToNextPage ?? defaultGoToNextPage)();
        break;
      case AddOnCustomizeStep.CFAR:
        processStep(!isCfarAvailable);
        break;
      default:
        break;
    }
  }, [currentStep, isCfarAvailable, goBack, goToNextPage, defaultGoBack, defaultGoToNextPage, setCurrentStep]);

  if (!(addOnCustomizeSteps[currentStep] === AddOnCustomizeStep.CFAR && isCfarAvailable && cfarQuoteV2 != null)) {
    return null;
  }
  return (
    <Box className="mobile-hotel-add-on-customize-root">
      <Box className="mobile-hotel-add-on-customize-container">
        {!isHtsCfarOffer(cfarQuoteV2) && (
            <StandAloneCfarDetails
              currentHotelCfarQuote={cfarQuoteV2}
              currentCancellationPolicy={cancellationPolicy}
              onContinue={onContinue}
              cardContentProps={{
                disablePartialScroll: true,
                onSelectValueCallback(option) {
                  trackEvent({
                    eventName: CAP1_HOTEL_CFAR_ADD_ON_CHOICE,
                    properties: {
                      ...cfarQuoteV2.trackingProperties.properties,
                      cfar_choice: option === CfarOption.Yes ? 1 : 0,
                      cfar_room_card_offer:
                        refundableRoomProductCorrespondingToChosenRoom ===
                        undefined
                          ? 0
                          : 1,
                      cfar_fee_is_refundable:
                        cfarQuoteV2.refundPremiumAtExercise,
                    },
                    ...(cfarQuoteV2.trackingProperties.encryptedProperties
                      ? {
                          encryptedProperties: [
                            cfarQuoteV2.trackingProperties.encryptedProperties,
                          ],
                        }
                      : {}),
                  });
                },
              }}
            />
          )}
          {isHtsCfarOffer(cfarQuoteV2) && (
            <Cap1HotelCfarHtsMerchandising 
              cfarRateQuote={cfarQuoteV2}
              onContinue={onContinue}
            />
          )}
      </Box>
    </Box>
  );
};
